<template>
  <div>
    <v-tour name="firstTimeTour" :steps="steps" :options="tourOptions" :callbacks="tourCallbacks">
      <template slot-scope="tour">
        <transition name="fade">
          <v-step
            v-if="tour.steps[tour.currentStep]"
            :key="tour.currentStep"
            :step="tour.steps[tour.currentStep]"
            :previous-step="tour.previousStep"
            :next-step="tour.nextStep"
            :stop="tour.stop"
            :skip="tour.skip"
            :is-first="tour.isFirst"
            :is-last="tour.isLast"
            :labels="tour.labels"
          >
            <template v-if="tour.currentStep === 0">
              <div class="v-step__header">
                <h3>Welcome to the Tibetan Dictionary!</h3>
              </div>
              <div class="v-step__content">
                <p>Let's take a quick tour to help you get started.</p>
              </div>
            </template>
            <template v-else>
              <div slot="actions">
                <button @click="tour.previousStep" v-if="!tour.isFirst" class="v-step__button v-step__button-previous">Previous</button>
                <button @click="tour.nextStep" v-if="!tour.isLast" class="v-step__button v-step__button-next">Next</button>
                <button @click="tour.stop" v-if="tour.isLast" class="v-step__button v-step__button-stop">Got it!</button>
              </div>
            </template>
          </v-step>
        </transition>
      </template>
    </v-tour>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'FirstTimeUserTutorial',
  data() {
    return {
      tourOptions: {
        useKeyboardNavigation: true,
        labels: {
          buttonSkip: 'Skip tutorial',
          buttonPrevious: 'Previous',
          buttonNext: 'Next',
          buttonStop: 'Got it!'
        },
        highlight: true,
        enabledButtons: {
          buttonSkip: true,
          buttonPrevious: true,
          buttonNext: true,
          buttonStop: true
        }
      },
      tourCallbacks: {
        onStop: this.handleTourEnd,
        onSkip: this.handleTourEnd,
        onFinish: this.handleTourEnd
      }
    };
  },
  computed: {
    ...mapState({
      hasSeenTutorial: state => state.app?.hasSeenTutorial
    }),
    steps() {
      return [
        {
          target: '.search-input-container',
          content: `Welcome to the Tibetan Dictionary! Understanding <strong>search types</strong> is key to using this dictionary effectively. Let's take a quick tour.`,
          params: {
            placement: 'bottom',
            highlight: true
          }
        },
        {
          target: '.search-type-dropdown',
          header: {
            title: 'Search Type Dropdown'
          },
          content: `This dropdown is very important! Different search types give you very different results. Click it to see the options.`,
          params: {
            placement: 'bottom',
            highlight: true
          }
        },
        {
          target: '.search-type-dropdown',
          header: {
            title: 'Search Types Explained'
          },
          content: `
            <ul style="text-align: left; padding-left: 20px; margin: 10px 0;">
              <li style="margin-bottom: 8px;"><strong>Defined Term</strong> - Search for Tibetan terms in dictionary entries (contains, begins with, exact)</li>
              <li style="margin-bottom: 8px;"><strong>Full Text</strong> - Search across all fields including definitions</li>
              <li style="margin-bottom: 8px;"><strong>Translation Tool</strong> - Break down sentences word-by-word</li>
            </ul>
          `,
          params: {
            placement: 'bottom',
            highlight: true
          }
        },
        {
          target: '.search-input-box',
          header: {
            title: 'Search Input'
          },
          content: `Type your search term here. Try experimenting with different search types to find what you're looking for!`,
          params: {
            placement: 'bottom',
            highlight: true
          }
        },
        {
          target: '.search-history-nav',
          header: {
            title: 'Search History'
          },
          content: `Your search history is saved here. You can quickly return to previous searches by clicking on any item in this dropdown.`,
          params: {
            placement: 'bottom',
            highlight: true
          }
        },
        {
          target: '.settings-nav',
          header: {
            title: 'Settings'
          },
          content: `Access settings here to customize your dictionary experience, including translation tool preferences, ignored terms, and more.`,
          params: {
            placement: 'bottom',
            highlight: true
          }
        },
        {
          target: '.search-input-container',
          header: {
            title: 'You are all set!'
          },
          content: `That is it! Start searching and exploring the dictionary. If you need help, look for the developer message or reach out.`,
          params: {
            placement: 'bottom',
            highlight: true
          }
        }
      ];
    }
  },
  mounted() {
    // TEMPORARY: Always show tutorial for development/testing
    // TODO: Re-enable hasSeenTutorial check before production
    // if (!this.hasSeenTutorial) {
      // Small delay to ensure DOM is ready
      //setTimeout(() => {
        //this.$tours['firstTimeTour'].start();
      //}, 800);
    //}
  },
  methods: {
    handleTourEnd() {
      // Mark tutorial as seen
      if (this.$store.commit) {
        this.$store.commit('app/setHasSeenTutorial', true);
      }
      this.$emit('tutorial-dismissed');
    }
  }
};
</script>

<style>
/* Override vue-tour default styles */
.v-tour__target--highlighted {
  box-shadow: 0 0 0 99999px rgba(0, 0, 0, 0.7) !important;
}

.v-step {
  background: linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%) !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3) !important;
}

.v-step__header {
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
  color: white;
}

.v-step__content {
  color: #2c3e50;
}

.v-step__content strong {
  color: #357abd;
}

.v-step__button {
  border-radius: 8px !important;
  transition: all 0.2s ease !important;
}

.v-step__button-skip {
  color: #6b7280 !important;
  background: transparent !important;
  border: 1px solid #d1d5db !important;
}

.v-step__button-skip:hover {
  background: #f3f4f6 !important;
}

.v-step__button-previous {
  color: #4a90e2 !important;
  background: white !important;
  border: 2px solid #4a90e2 !important;
}

.v-step__button-previous:hover {
  background: #f0f7ff !important;
  transform: translateY(-2px) !important;
}

.v-step__button-next,
.v-step__button-stop {
  color: white !important;
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%) !important;
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3) !important;
}

.v-step__button-next:hover,
.v-step__button-stop:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(74, 144, 226, 0.4) !important;
}

.v-step__arrow {
  border-color: #ffffff !important;
}

/* Fade transition */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
